import React, { useState, useCallback, useEffect, useRef } from 'react';
import { ThemeProvider } from '@elementor/ui/styles';
import CssBaseline from '@elementor/ui/CssBaseline';
import Box from '@elementor/ui/Box';
import { SearchPage, ResultsPage } from './components';
import { askAgent, ERROR_MESSAGES, pushDataLayerEvent } from './helpers';
import { LIMITS } from './config';

// Whitelist of messages that are safe to render in the UI. Anything else
// (e.g. an unexpected TypeError from a browser/runtime quirk) falls back to
// GENERIC_ERROR so customers never see raw JS error text.
const SAFE_ERROR_MESSAGES = new Set(Object.values(ERROR_MESSAGES));

function App() {
  const [currentPage, setCurrentPage] = useState('search');
  const [query, setQuery] = useState('');
  const [response, setResponse] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const abortRef = useRef(null);

  // Expose current page state as a class on #ft-root so Elementor can react via CSS
  // (the "FT add class" WP snippet mirrors this onto the surrounding hero).
  useEffect(() => {
    const root = document.getElementById('ft-root');
    if (!root) return;

    if (currentPage === 'results') {
      root.classList.add('ft-search-active');
    } else {
      root.classList.remove('ft-search-active');
    }
  }, [currentPage]);

  const handleSearch = useCallback(async (searchQuery, source = null) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const signal = AbortSignal.any
      ? AbortSignal.any([controller.signal, AbortSignal.timeout(LIMITS.requestTimeoutMs)])
      : controller.signal;

    if (!AbortSignal.any) {
      // Fallback for browsers without AbortSignal.any: wall-clock timeout via setTimeout.
      setTimeout(() => controller.abort(new DOMException('Timeout', 'TimeoutError')), LIMITS.requestTimeoutMs);
    }

    setIsLoading(true);
    setError(null);
    setResponse(null);
    setQuery(searchQuery);
    setCurrentPage('results');

    try {
      const data = await askAgent(searchQuery, { signal });
      if (source === 'hero') {
        pushDataLayerEvent({
          section: 'hero section',
          element_name: 'ai search button',
          outcome: 'searching information',
          search_term: searchQuery,
          level_1: data.answer || 'No answer',
        });
      } else if (source === 'chip') {
        pushDataLayerEvent({
          section: 'hero section',
          element_name: 'search tag button',
          outcome: 'searching information',
          english_text: searchQuery,
          search_term: searchQuery,
          level_1: data.answer || 'No answer',
        });
      } else if (source === 'results') {
        pushDataLayerEvent({
          section: 'in page',
          element_name: 'search button',
          outcome: 'searching information',
          search_term: searchQuery,
          level_1: data.answer || 'No answer',
        });
      }
      setResponse(data);
    } catch (err) {
      if (err?.name === 'AbortError') return;
      if (err?.name === 'TimeoutError') {
        setError(ERROR_MESSAGES.TIMEOUT);
      } else {
        console.error('Error fetching response:', err);
        const safeMessage = SAFE_ERROR_MESSAGES.has(err?.message)
          ? err.message
          : ERROR_MESSAGES.GENERIC_ERROR;
        setError(safeMessage);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleBackToSearch = useCallback(() => {
    abortRef.current?.abort();
    setCurrentPage('search');
    setQuery('');
    setResponse(null);
    setError(null);
  }, []);

  return (
    <ThemeProvider colorScheme="light">
      <CssBaseline />
      <Box sx={{ bgcolor: 'background.default' }}>
        {currentPage === 'search' ? (
          <SearchPage
            onSearch={handleSearch}
            isLoading={isLoading}
          />
        ) : (
          <ResultsPage
            query={query}
            response={response}
            onNewSearch={handleSearch}
            onBackToSearch={handleBackToSearch}
            isLoading={isLoading}
            error={error}
          />
        )}
      </Box>
    </ThemeProvider>
  );
}

export default App;
